import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const { login, resendCode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`${t("auth.welcome")}, ${user.name?.split(" ")[0]}`);
      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && typeof detail === "string" && detail.includes("EMAIL_NOT_VERIFIED")) {
        try { await resendCode(email.trim()); } catch (_) { /* ignore */ }
        navigate("/verify-email", { state: { email: email.trim(), notVerified: true } });
        return;
      }
      toast.error(t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center mb-3">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-stone-text">{t("auth.loginTitle")}</h1>
          <p className="text-stone-muted text-sm">{t("auth.loginSubtitle")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.email")}</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.password")}</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60"
          >
            {loading ? t("auth.loggingIn") : t("auth.loginBtn")}
          </button>
        </form>

        <p className="text-center text-sm text-stone-muted mt-5">
          {t("auth.noAccount")}{" "}
          <Link to="/register" data-testid="login-to-register" className="text-brand font-semibold hover:underline">
            {t("nav.signup")}
          </Link>
        </p>
      </div>
    </div>
  );
}
